import React from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Scale,
  ShieldCheck,
  FileText,
  Handshake,
  RefreshCw,
  CheckCircle2,
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';

const HERO_IMAGE =
  'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/0zcyjrv9_MD%20HERO%20IMAGE.png';
const SUPPLY_IMAGE =
  'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/hc0kurg5_ADDITIONAL%20IMAGE.png';

const PRINCIPLES = [
  { icon: ShieldCheck, title: 'Prevention',  body: 'Preventing modern slavery, forced labour, human trafficking and unethical working practices.' },
  { icon: FileText,    title: 'Compliance',  body: 'Internal policies and operational controls designed to promote integrity, transparency and responsible business conduct.' },
  { icon: Handshake,   title: 'Ethical Partnerships', body: 'Working with suppliers and partners that share our commitment to ethical business operations and lawful employment practices.' },
];

const Eyebrow = ({ children }) => (
  <p className="text-[11px] font-bold text-[#ef5b25] uppercase tracking-[0.22em] mb-3">{children}</p>
);

const SectionHeading = ({ eyebrow, title }) => (
  <div className="mb-8">
    <Eyebrow>{eyebrow}</Eyebrow>
    <h2 className="text-3xl sm:text-4xl lg:text-[2.5rem] font-bold leading-tight tracking-tight text-[#0a1929]">{title}</h2>
    <div className="h-1 w-20 bg-[#ef5b25] rounded-full mt-4" />
  </div>
);

const AboutModernSlavery = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromTopNav = location.state?.from === 'top-nav';

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="about-modern-slavery-page">
      <Header />
      <div className="h-20" />

      {/* ╔═════════════════════ HERO ═════════════════════╗ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#f7f9fc] via-white to-[#eef3f8]" data-testid="ms-hero">
        <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#1F4E79]/8 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-40 -left-32 w-96 h-96 rounded-full bg-[#ef5b25]/8 blur-3xl" aria-hidden="true" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 sm:pt-16 sm:pb-24">
          <button
            onClick={() => (fromTopNav ? navigate('/') : navigate(-1))}
            className="flex items-center gap-2 text-gray-600 hover:text-[#1F4E79] font-medium mb-10 transition-colors duration-200"
            data-testid="ms-back-button"
          >
            <ArrowLeft size={18} />
            {fromTopNav ? 'Back to Home' : 'Back'}
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-white/80 via-white/95 to-white/80 backdrop-blur-md border border-white/80 text-[#1F4E79] text-[11px] font-bold uppercase tracking-[0.22em] mb-6 shadow-[0_4px_20px_-2px_rgba(31,78,121,0.18)] ring-1 ring-inset ring-white/60">
                <Scale size={13} className="text-[#ef5b25]" strokeWidth={2} />
                Modern Slavery Statement
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-[64px] font-bold text-[#0a1929] leading-[1.05] tracking-tight mb-6" data-testid="ms-title">
                Ethical Business.<br />
                <span className="text-[#1F4E79]">Responsible</span> Operations.
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl mb-10 font-medium">
                eCOMM is committed to conducting business ethically and responsibly across all areas of operation and supply chain engagement.
              </p>

              <div className="flex">
                <Link
                  to="/contact-sales"
                  onClick={() => window.__resetSourceScroll?.()}
                  className="group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-[#1F4E79] text-white font-bold rounded-lg hover:bg-[#163a5f] hover:gap-3.5 transition-all duration-200 shadow-md hover:shadow-lg"
                  data-testid="ms-cta"
                >
                  Contact Us</Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative max-w-md mx-auto lg:mx-0">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#1F4E79]/15 to-[#ef5b25]/15 rounded-3xl blur-2xl pointer-events-none" aria-hidden="true" />
                <div className="relative rounded-2xl overflow-hidden border border-white/60 shadow-2xl">
                  <img
                    src={HERO_IMAGE}
                    alt="Business professionals joining hands together in unity"
                    className="w-full h-auto object-cover aspect-square"
                    data-testid="ms-hero-image"
                  />
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#0a1929]/90 via-[#0a1929]/55 to-transparent p-5 pt-14">
                    <p className="text-[10px] font-bold text-[#ffcba6] uppercase tracking-[0.2em] mb-1">United In Standards</p>
                    <p className="text-white font-bold text-xl leading-tight">Operating With Integrity.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ OUR COMMITMENT ═════════════════════╗ */}
      <section id="commitment" className="bg-white py-20 sm:py-24" data-testid="ms-commitment">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Our Commitment" title="Preventing exploitation. Protecting people." />
          </div>
          <div className="lg:col-span-7 space-y-5 text-gray-700 leading-relaxed text-base sm:text-lg">
            <p>
              eCOMM Merchant Solutions is committed to preventing modern slavery, forced labour, human trafficking and unethical working practices within our business operations and supply chain relationships.
            </p>
            <p>
              We believe all organisations have a responsibility to promote ethical business conduct and support fair, lawful and safe working environments.
            </p>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ THREE PRINCIPLES ═════════════════════╗ */}
      <section className="bg-[#f7f9fc] py-20 sm:py-24" data-testid="ms-principles">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12 text-center mx-auto">
            <Eyebrow>Three Core Principles</Eyebrow>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.5rem] font-bold leading-tight tracking-tight text-[#0a1929]">How We Operate Responsibly.</h2>
            <div className="h-1 w-20 bg-[#ef5b25] rounded-full mt-4 mx-auto" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {PRINCIPLES.map((p, i) => (
              <div
                key={i}
                className="group relative rounded-2xl bg-gradient-to-br from-white via-white to-[#f0f5fa] border border-gray-100 shadow-[0_10px_30px_-8px_rgba(31,78,121,0.18)] hover:shadow-[0_18px_38px_-8px_rgba(31,78,121,0.28)] hover:-translate-y-1 hover:border-[#1F4E79]/25 p-7 transition-all duration-300 overflow-hidden"
                data-testid={`principle-card-${i}`}
              >
                <h3 className="font-bold text-[#0a1929] text-lg leading-snug mb-3">{p.title}</h3>
                <p className="text-[14px] text-gray-600 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ BUSINESS OPERATIONS & SUPPLY CHAIN — split with image ═════════════════════╗ */}
      <section className="bg-white py-20 sm:py-24" data-testid="ms-supply-chain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-emerald-400/25 to-[#1F4E79]/15 rounded-3xl blur-2xl pointer-events-none" aria-hidden="true" />
              <div className="relative rounded-2xl overflow-hidden border border-white/60 shadow-2xl">
                <img
                  src={SUPPLY_IMAGE}
                  alt="Responsible investment in growth — hand watering a seedling growing from coins"
                  className="w-full h-auto object-cover aspect-[4/3]"
                  data-testid="supply-image"
                />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur-sm shadow-md text-[#1F4E79] text-[10px] font-bold uppercase tracking-[0.18em]">
                  <Handshake size={12} className="text-[#ef5b25]" strokeWidth={2.5} />
                  Responsible Supply Chain
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <SectionHeading eyebrow="Business Operations & Supply Chain" title="Built on responsible relationships." />
            <p className="text-lg text-gray-700 leading-relaxed mb-5">
              eCOMM operates within the payments technology sector, providing payment processing and commerce solutions to businesses across multiple industries.
            </p>
            <p className="text-base text-gray-700 leading-relaxed">
              While the nature of our business presents a relatively low risk of modern slavery exposure, we recognise the importance of maintaining responsible supplier relationships and appropriate due diligence processes.
            </p>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ POLICIES & GOVERNANCE / SUPPLIER EXPECTATIONS — dark contrast ═════════════════════╗ */}
      <section className="bg-gradient-to-br from-[#0a1929] via-[#0e2540] to-[#163a5f] py-20 sm:py-24 relative overflow-hidden" data-testid="ms-governance">
        <div className="pointer-events-none absolute -top-32 right-0 w-96 h-96 rounded-full bg-[#ef5b25]/10 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#1F4E79]/30 blur-3xl" aria-hidden="true" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <p className="text-[11px] font-bold text-[#ffcba6] uppercase tracking-[0.22em] mb-3">Policies & Governance</p>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.5rem] font-bold text-white leading-tight tracking-tight mb-4">Standards we hold ourselves — and our partners — to.</h2>
            <div className="h-1 w-20 bg-[#ef5b25] rounded-full" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 p-7 hover:bg-white/15 transition-all duration-200" data-testid="policy-card-internal">
              <div className="w-11 h-11 rounded-lg bg-[#ef5b25]/20 border border-[#ef5b25]/30 flex items-center justify-center mb-5">
                <FileText size={20} className="text-[#ffcba6]" strokeWidth={1.75} />
              </div>
              <h3 className="text-white font-bold text-xl mb-3 leading-tight">Internal Policies & Controls</h3>
              <p className="text-white/85 text-[15px] leading-relaxed mb-4">
                We are committed to maintaining professional standards across our business operations and expect our suppliers, partners and service providers to operate in accordance with applicable laws and ethical business practices.
              </p>
              <p className="text-white/75 text-[14px] leading-relaxed">
                eCOMM supports internal policies and operational controls designed to promote integrity, transparency and responsible business conduct.
              </p>
            </div>

            <div className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 p-7 hover:bg-white/15 transition-all duration-200" data-testid="policy-card-supplier">
              <div className="w-11 h-11 rounded-lg bg-[#ef5b25]/20 border border-[#ef5b25]/30 flex items-center justify-center mb-5">
                <Handshake size={20} className="text-[#ffcba6]" strokeWidth={1.75} />
              </div>
              <h3 className="text-white font-bold text-xl mb-3 leading-tight">Supplier Expectations</h3>
              <p className="text-white/85 text-[15px] leading-relaxed mb-4">
                We seek to work with suppliers and partners that share our commitment to ethical business operations, lawful employment practices and professional integrity.
              </p>
              <p className="text-white/75 text-[14px] leading-relaxed">
                Where appropriate, supplier relationships may be reviewed as part of our ongoing operational and compliance processes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ BROADER COMMITMENTS ═════════════════════╗ */}
      <section className="py-20 sm:py-24 bg-white" data-testid="ms-broader-commitments">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <SectionHeading
              eyebrow="How We Operate"
              title="Our broader commitments."
            />
            <p className="text-lg text-gray-700 leading-relaxed">
              Ethical business is more than a single policy. Below are the commitments that
              shape how we work — from the people we hire and the customers we serve, to the
              regulatory frameworks that hold us accountable.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="ethics-commitments">
            {[
              {
                title: 'Diversity, Equity & Inclusion',
                body: 'We believe that diverse perspectives strengthen our business and help us better serve our customers. We are committed to fostering a workplace where everyone is treated with dignity, respect and equal opportunity, regardless of background, identity or experience.',
                testId: 'commit-dei',
              },
              {
                title: 'Fair Treatment & Customer Focus',
                body: 'We are committed to treating customers fairly and delivering products and services that are transparent, secure and aligned with their needs. We strive to provide clear communication, responsive support and a customer-first approach across every interaction.',
                testId: 'commit-fair-treatment',
              },
              {
                title: 'Security, Compliance & Governance',
                body: 'As a regulated payment provider, we maintain robust governance frameworks and security standards designed to protect customers, employees and stakeholders. Our commitment to compliance and accountability underpins everything we do.',
                testId: 'commit-security',
              },
              {
                title: 'Doing Business The Right Way',
                body: 'Ethical business practices are fundamental to how we operate. From customer care and regulatory compliance to workplace culture and responsible partnerships, we are committed to maintaining the trust placed in us by merchants, employees and partners alike.',
                testId: 'commit-right-way',
              },
            ].map((c) => (
              <div
                key={c.testId}
                data-testid={c.testId}
                className="relative rounded-xl bg-white border border-gray-100 p-7 sm:p-8 shadow-sm hover:shadow-xl hover:border-[#1F4E79]/20 transition-all duration-200"
              >
                <h3 className="font-bold text-[#0a1929] text-xl leading-tight mb-3">
                  {c.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ╔═════════════════════ CONTINUOUS REVIEW ═════════════════════╗ */}
      <section className="py-16 sm:py-20 bg-[#f7f9fc]" data-testid="ms-continuous-review">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-white border border-gray-100 p-8 sm:p-12 shadow-lg relative overflow-hidden">
            <div className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#ef5b25]/8 blur-3xl" aria-hidden="true" />
            <div className="relative">
              <Eyebrow>Continuous Review</Eyebrow>
              <h3 className="text-2xl sm:text-3xl font-bold text-[#0a1929] leading-tight mb-4">An evolving commitment.</h3>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-3">
                eCOMM will continue to review and strengthen its approach to ethical business practices and responsible supplier engagement as part of its ongoing governance and compliance framework.
              </p>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed italic">
                This statement is reviewed periodically and reflects eCOMM's commitment to ethical business operations and responsible corporate conduct.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ CTA STRIP ═════════════════════╗ */}
      <section className="py-16 sm:py-20 bg-white" data-testid="ms-final-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-gradient-to-br from-[#0a1929] via-[#0e2540] to-[#163a5f] p-8 sm:p-14 text-center shadow-xl relative overflow-hidden">
            <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#ef5b25]/15 blur-3xl" aria-hidden="true" />
            <div className="pointer-events-none absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#1F4E79]/25 blur-3xl" aria-hidden="true" />
            <div className="relative">
              <h3 className="text-3xl sm:text-4xl lg:text-[2.5rem] font-bold text-white leading-tight tracking-tight mb-4">
                Questions About This Page?
              </h3>
              <p className="text-white/90 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
                Get in touch with our team to discuss eCOMM's approach to ethical business practices and supplier engagement.
              </p>
              <div className="flex justify-center">
                <Link
                  to="/contact-sales"
                  onClick={() => window.__resetSourceScroll?.()}
                  className="group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-white text-[#1F4E79] font-bold rounded-lg hover:bg-white/90 hover:gap-3.5 transition-all duration-200 shadow-md hover:shadow-lg"
                  data-testid="ms-cta-final"
                >
                  Contact Us</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default AboutModernSlavery;
